/**
 * Emperor Marble — Storefront block-code search interceptor
 * ==========================================================
 *
 * PROBLEM (verified live 2026-06-04)
 *   Searching a bare block code on the storefront — e.g.
 *   `emperormarble.co.uk/search?q=18011` (also 18010 / 18020 / 18021) —
 *   returns the WRONG product ("White Onyx Slabs"). Block codes are NOT
 *   product names; Shopify native product search is fuzzy/boosted and
 *   mis-resolves a bare code onto a boosted product. Control codes
 *   (18012, 18099, 99999) correctly return nothing, so the bad matches are
 *   a discrete enumerated set wired into Shopify Search & Discovery.
 *
 * DURABLE FIX (this file)
 *   Before Shopify's fuzzy search ever runs, classify the query:
 *     - block-code-like (single alphanumeric+hyphen token with a digit:
 *       18011, B1820, EM-CAL-001) -> resolve it against the FMM canonical
 *       inventory endpoint (exact match, NO fuzzy fallback). A known code
 *       routes the shopper to the CORRECT stone; an unknown code shows an
 *       honest "no match" instead of fuzzy-falling onto White Onyx.
 *     - everything else (stone names, free text) -> leave Shopify native
 *       product search completely untouched.
 *
 *   This remains robust even if the Search & Discovery term boosts are
 *   re-introduced later: block codes never reach Shopify's fuzzy matcher.
 *
 * SOURCE OF TRUTH
 *   `isBlockCodeLike` below is a VERBATIM port of
 *   `src/lib/shopify/search-adapter.ts` in the findmymarble-aod repo.
 *   If you change the classifier there, mirror it here (see README.md
 *   "Keeping the classifier in sync").
 *
 * ENDPOINT WIRING (Emperor Marble theme specific)
 *   The endpoint base is read at call time from `window.__FINDMYMARBLE_API_URL`
 *   (set in layout/theme.liquid from `settings.findmymarble_api_url`), so the
 *   same FMM host the rest of the theme talks to is reused. If that global is
 *   absent we fall back to the canonical production host. Path is always
 *   `/api/shopify/block-search?q=<query>`.
 *
 * DEPENDENCY-FREE
 *   Vanilla ES2017. No framework, no bundler, no imports. Drops straight
 *   into a theme asset and works in any browser the storefront supports.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  var CONFIG = {
    /**
     * Canonical production host used ONLY as a fail-safe when
     * `window.__FINDMYMARBLE_API_URL` is not set. In normal operation the base
     * comes from that global (see resolveEndpoint). CORS on the FMM endpoint
     * allow-lists emperormarble.co.uk / www.emperormarble.co.uk.
     */
    fallbackBase: 'https://findmymarble.com',

    /** Paths appended to the resolved FMM API base. */
    path: '/api/shopify/block-search',
    stoneTypesPath: '/api/shopify/stone-types',
    stoneBlocksPath: '/api/shopify/blocks',

    /**
     * What to do when a block code resolves to a known stone.
     *
     *  'redirect-to-block' (default):
     *     Resolve the stone name to a Shopify product URL, then deep-link to the
     *     exact inventory card hash rendered by the product page:
     *     18011 -> FMM says "Patagonia" -> /products/patagonia#block-<uuid>
     *     If Shopify product URL resolution fails, fail open to /search?q=<stone>.
     *
     *  'render-inline':
     *     Render a result card in #block-search-result (see renderInline()).
     *     Use when you want code-level detail (block code, dimensions, sqm)
     *     shown directly rather than bouncing to the product.
     */
    onMatch: 'redirect-to-block',

    /** Container the 'render-inline' mode writes into, and where "no match" notices go. */
    resultContainerId: 'block-search-result',

    /** Suppress native fuzzy results while a canonical block-code answer is shown. */
    nativeResultsSelector: '#ProductGridContainer, .search__form-text',

    /** Request timeout (ms). On timeout/error we FAIL OPEN to native search. */
    timeoutMs: 4000,

    /** Predictive dropdown lookup debounce (ms) for networked block-code lookups. */
    predictiveDebounceMs: 225,

    /** Mobile-first caps for dropdown inventory sections. */
    stoneResultLimit: 12,
    popularStoneLimit: 6,
    inlineBlockInitialLimit: 20,
    inlineBlockChunkSize: 20,
  };

  var productUrlCache = {};
  var stoneTypesCache = null;
  var stoneTypesRequest = null;
  var stoneBlocksCache = {};
  var predictiveState = {
    controller: null,
    timer: null,
    sequence: 0,
  };

  /**
   * Resolve the full endpoint base from the theme-provided global, falling back
   * to the canonical production host. Trailing slashes are trimmed so the path
   * concatenation never doubles up.
   */
  function resolveApiBase() {
    var base = (typeof window !== 'undefined' && window.__FINDMYMARBLE_API_URL)
      ? String(window.__FINDMYMARBLE_API_URL)
      : CONFIG.fallbackBase;
    return base.replace(/\/+$/, '');
  }

  function resolveApiUrl(path) {
    return resolveApiBase() + path;
  }

  function resolveEndpoint() {
    return resolveApiUrl(CONFIG.path);
  }

  // ---------------------------------------------------------------------------
  // Classifier — VERBATIM from src/lib/shopify/search-adapter.ts
  // ---------------------------------------------------------------------------

  /**
   * Decide whether a raw search query looks like a block code.
   *
   * Block codes are short identifiers that contain at least one digit and may
   * carry ONE internal separator between a prefix and a number — e.g. `18011`,
   * `B1820`, `EM-CAL-001`, and crucially `SG 13508` (the real on-product format
   * a shopper reads and types). Stone names / product text are rejected because
   * they are purely alphabetic (`White Onyx`, `Patagonia`) or too long.
   *
   * VERBATIM from src/lib/shopify/search-adapter.ts — keep in sync.
   */
  function isBlockCodeLike(raw) {
    var q = (raw == null ? '' : String(raw)).trim();
    if (!q) return false;
    // A block code MUST carry at least one digit (stone names are alphabetic).
    if (!/\d/.test(q)) return false;
    // Identifiers are short; reject long free text even if it contains a number.
    if (q.length > 24) return false;
    // Allowed shape: alphanumeric tokens joined by single separators (space or
    // hyphen): 18011, B1820, EM-CAL-001, SG 13508.
    return /^[A-Za-z0-9]+(?:[\s-][A-Za-z0-9]+)*$/.test(q);
  }

  // ---------------------------------------------------------------------------
  // FMM canonical lookup
  // ---------------------------------------------------------------------------

  /**
   * Resolve a block-code-like query against the FMM canonical inventory.
   * Returns the parsed ShopifyBlockSearchResponse, or null on network failure.
   */
  function lookupBlockCode(query, options) {
    options = options || {};
    var url = resolveEndpoint() + '?q=' + encodeURIComponent(query);
    var controller = !options.signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, CONFIG.timeoutMs)
      : null;

    return fetch(url, {
      method: 'GET',
      // The endpoint is CORS-allow-listed for this origin; no credentials needed.
      headers: { Accept: 'application/json' },
      signal: options.signal || (controller ? controller.signal : undefined),
    })
      .then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        return null; // FAIL OPEN — caller falls through to native search.
      });
  }

  function normalizeInventoryTerm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function normalizeStoneSearchTerm(value) {
    var text = String(value == null ? '' : value);
    if (typeof text.normalize === 'function') text = text.normalize('NFD');
    return text
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function stoneSearchTokens(term) {
    return term ? term.split(' ').filter(Boolean) : [];
  }

  function levenshteinDistance(a, b) {
    if (a === b) return 0;
    if (!a) return b.length;
    if (!b) return a.length;

    var previous = [];
    var current = [];
    for (var i = 0; i <= b.length; i++) previous[i] = i;

    for (var j = 1; j <= a.length; j++) {
      current[0] = j;
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(j - 1) === b.charAt(k - 1) ? 0 : 1;
        current[k] = Math.min(
          current[k - 1] + 1,
          previous[k] + 1,
          previous[k - 1] + cost
        );
      }
      var swap = previous;
      previous = current;
      current = swap;
    }

    return previous[b.length];
  }

  function fuzzyStoneThreshold(term) {
    return Math.max(1, Math.min(3, Math.floor(term.length / 4)));
  }

  function fuzzyStoneDistance(queryTerm, nameTerm) {
    if (!queryTerm || queryTerm.length < 4 || !nameTerm) return null;
    var threshold = fuzzyStoneThreshold(queryTerm);
    var candidates = [nameTerm].concat(stoneSearchTokens(nameTerm));
    var best = null;

    for (var i = 0; i < candidates.length; i++) {
      var candidate = candidates[i];
      if (!candidate) continue;
      if (Math.abs(candidate.length - queryTerm.length) > threshold) continue;
      var distance = levenshteinDistance(queryTerm, candidate);
      if (distance <= threshold && (best === null || distance < best)) best = distance;
    }

    return best;
  }

  function hasAllStoneTokens(nameTerm, queryTokens) {
    if (!nameTerm || queryTokens.length < 2) return false;
    for (var i = 0; i < queryTokens.length; i++) {
      if (nameTerm.indexOf(queryTokens[i]) === -1) return false;
    }
    return true;
  }

  function readStoneTypes(data) {
    var stones = data && data.stone_types && data.stone_types.length ? data.stone_types : [];
    return stones.filter(function (stone) {
      return stone && stone.stone_name;
    });
  }

  function ensureStoneTypes() {
    if (stoneTypesCache) return Promise.resolve(stoneTypesCache);
    if (stoneTypesRequest) return stoneTypesRequest;

    stoneTypesRequest = fetch(resolveApiUrl(CONFIG.stoneTypesPath), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (data) {
        stoneTypesCache = readStoneTypes(data);
        return stoneTypesCache;
      })
      .catch(function () {
        stoneTypesCache = [];
        return stoneTypesCache;
      });

    return stoneTypesRequest;
  }

  function lookupBlocksForStone(stoneName) {
    var stone = String(stoneName || '').trim();
    var cacheKey = normalizeInventoryTerm(stone);
    if (!stone) return Promise.resolve([]);
    if (stoneBlocksCache[cacheKey]) return stoneBlocksCache[cacheKey];

    stoneBlocksCache[cacheKey] = fetch(
      resolveApiUrl(CONFIG.stoneBlocksPath) + '?stone_name=' + encodeURIComponent(stone),
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      }
    )
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.blocks) return [];
        return uniqueBlocks(data.blocks);
      })
      .catch(function () {
        delete stoneBlocksCache[cacheKey];
        return null;
      });

    return stoneBlocksCache[cacheKey];
  }

  function filterStoneTypes(query) {
    var q = normalizeStoneSearchTerm(query);
    if (!q || !stoneTypesCache || !stoneTypesCache.length) return [];
    var queryTokens = stoneSearchTokens(q);
    var exactMatches = [];
    var prefixMatches = [];
    var substringMatches = [];
    var tokenMatches = [];
    var fuzzyMatches = [];

    for (var i = 0; i < stoneTypesCache.length; i++) {
      var stone = stoneTypesCache[i];
      var name = normalizeStoneSearchTerm(stone.stone_name);
      var category = normalizeStoneSearchTerm(stone.stone_category);
      if (!name && !category) continue;
      if (name === q) {
        exactMatches.push(stone);
      } else if (name.indexOf(q) === 0) {
        prefixMatches.push(stone);
      } else if (name.indexOf(q) !== -1 || category.indexOf(q) !== -1) {
        substringMatches.push(stone);
      } else if (hasAllStoneTokens(name, queryTokens)) {
        tokenMatches.push(stone);
      } else {
        var distance = fuzzyStoneDistance(q, name);
        if (distance !== null) fuzzyMatches.push({ stone: stone, distance: distance, name: name });
      }
    }

    fuzzyMatches.sort(function (a, b) {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });

    return exactMatches
      .concat(prefixMatches, substringMatches, tokenMatches)
      .concat(
        fuzzyMatches.map(function (match) {
          return match.stone;
        })
      )
      .slice(0, CONFIG.stoneResultLimit);
  }

  // ---------------------------------------------------------------------------
  // Outcomes
  // ---------------------------------------------------------------------------

  function getContainer() {
    return document.getElementById(CONFIG.resultContainerId);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fallbackStoneSearchUrl(stoneName) {
    return '/search?q=' + encodeURIComponent(stoneName || '');
  }

  function blockHash(block) {
    return block && block.id ? '#block-' + encodeURIComponent(block.id) : '';
  }

  function appendHash(url, hash) {
    if (!url || !hash) return url;
    return url.split('#')[0] + hash;
  }

  function normalizeTitle(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function readSuggestProducts(data) {
    return (((data || {}).resources || {}).results || {}).products || [];
  }

  function resolveProductUrl(stoneName, options) {
    var stone = String(stoneName || '').trim();
    var cacheKey = normalizeTitle(stone);
    if (!stone) return Promise.resolve(null);
    if (productUrlCache[cacheKey]) return productUrlCache[cacheKey];

    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(stone) +
      '&resources[type]=product&resources[limit]=3';

    productUrlCache[cacheKey] = fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options && options.signal ? options.signal : undefined,
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        var products = readSuggestProducts(data);
        if (!products.length) return null;
        var wanted = normalizeTitle(stone);
        var chosen = products[0];
        for (var i = 0; i < products.length; i++) {
          if (normalizeTitle(products[i].title) === wanted) {
            chosen = products[i];
            break;
          }
        }
        return chosen && chosen.url ? chosen.url : null;
      })
      .then(function (productUrl) {
        if (!productUrl) delete productUrlCache[cacheKey];
        return productUrl;
      })
      .catch(function () {
        delete productUrlCache[cacheKey];
        return null;
      });

    return productUrlCache[cacheKey];
  }

  function resolveBlockUrl(block, query, options) {
    var stone = block && block.stone_name ? block.stone_name : query;
    return resolveProductUrl(stone, options).then(function (productUrl) {
      if (!productUrl) return fallbackStoneSearchUrl(stone);
      return appendHash(productUrl, blockHash(block));
    });
  }

  function uniqueBlocks(blocks) {
    var seen = {};
    var out = [];
    for (var i = 0; i < (blocks || []).length; i++) {
      var block = blocks[i];
      var key = block && (block.id || block.block_code || i);
      if (seen[key]) continue;
      seen[key] = true;
      out.push(block);
    }
    return out;
  }

  /** A known block code resolved to a stone — route the shopper to it. */
  function handleMatch(query, block) {
    if (CONFIG.onMatch === 'render-inline') {
      renderInline(query, block);
      return;
    }
    resolveBlockUrl(block, query).then(function (url) {
      window.location.assign(url);
    });
  }

  /** Render a minimal result card (only used when onMatch === 'render-inline'). */
  function renderInline(query, block) {
    var el = getContainer();
    if (!el) {
      // No container wired — fall back to the safe redirect so the shopper
      // still lands on the right product.
      resolveBlockUrl(block, query).then(function (url) {
        window.location.assign(url);
      });
      return;
    }
    renderBlockList(el, query, [block]);
  }

  /**
   * A block-code-like query with NO canonical match. This is the critical
   * case: we MUST show an honest "no result" and must NOT let Shopify fuzzy
   * search take over (that is exactly the 18010 -> White Onyx bug).
   */
  function handleNoMatch(query) {
    var el = getContainer() || createResultsPageContainer();
    if (el) {
      renderNoMatch(el, query);
    }
    suppressNativeResults();
  }

  function suppressNativeResults() {
    if (!CONFIG.nativeResultsSelector) return;
    var nodes = document.querySelectorAll(CONFIG.nativeResultsSelector);
    for (var i = 0; i < nodes.length; i++) nodes[i].hidden = true;
  }

  function createResultsPageContainer() {
    var el = getContainer();
    if (el) return el;
    el = document.createElement('div');
    el.id = CONFIG.resultContainerId;
    el.hidden = true;
    var target = document.getElementById('ProductGridContainer') || document.querySelector('.template-search__header');
    if (target && target.parentNode) {
      target.parentNode.insertBefore(el, target);
    } else {
      document.body.appendChild(el);
    }
    return el;
  }

  function blockImage(block) {
    return block && block.images && block.images[0] ? block.images[0] : '';
  }

  function formatInventoryNumber(value) {
    if (value == null || value === '') return '';
    var number = Number(value);
    if (!isNaN(number)) {
      return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  }

  function inventoryMetric(value) {
    var number = Number(value);
    return isNaN(number) ? 0 : number;
  }

  function popularStoneTypes() {
    if (!stoneTypesCache || !stoneTypesCache.length) return [];
    return stoneTypesCache
      .slice()
      .sort(function (a, b) {
        var blockDelta = inventoryMetric(b.block_count) - inventoryMetric(a.block_count);
        if (blockDelta) return blockDelta;
        var sqmDelta = inventoryMetric(b.total_available_sqm) - inventoryMetric(a.total_available_sqm);
        if (sqmDelta) return sqmDelta;
        var aName = normalizeStoneSearchTerm(a.stone_name);
        var bName = normalizeStoneSearchTerm(b.stone_name);
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      })
      .slice(0, CONFIG.popularStoneLimit);
  }

  function stoneMetaMarkup(stone) {
    var parts = [];
    if (stone && stone.stone_category) parts.push(escapeHtml(stone.stone_category));
    if (stone && stone.block_count != null) {
      parts.push(escapeHtml(formatInventoryNumber(stone.block_count)) + ' blocks');
    }
    if (stone && stone.total_available_sqm != null) {
      parts.push(escapeHtml(formatInventoryNumber(stone.total_available_sqm)) + ' m²');
    }
    return parts.join(' · ');
  }

  function stoneRowMarkup(stone) {
    var name = stone && stone.stone_name ? stone.stone_name : '';
    var meta = stoneMetaMarkup(stone);
    return (
      '<a class="block-search-stone" href="' +
      escapeHtml(fallbackStoneSearchUrl(name)) +
      '" data-block-search-stone="' +
      escapeHtml(name) +
      '">' +
      '<span class="block-search-stone__body">' +
      '<span class="block-search-stone__name">' +
      escapeHtml(name) +
      '</span>' +
      (meta ? '<span class="block-search-stone__meta">' + meta + '</span>' : '') +
      '</span>' +
      '</a>'
    );
  }

  function blockCardMarkup(block, href) {
    var img = blockImage(block);
    var code = block && block.block_code ? block.block_code : '';
    var stone = block && block.stone_name ? block.stone_name : '';
    var sqm = block && block.available_sqm != null ? block.available_sqm : null;
    return (
      '<a class="block-search-card" href="' +
      escapeHtml(href) +
      '" data-block-code="' +
      escapeHtml(code) +
      '">' +
      (img
        ? '<img class="block-search-card__img" src="' +
          escapeHtml(img) +
          '" alt="' +
          escapeHtml(stone) +
          '" loading="lazy">'
        : '<span class="block-search-card__img block-search-card__img--empty" aria-hidden="true"></span>') +
      '<span class="block-search-card__body">' +
      '<span class="block-search-card__code">Block ' +
      escapeHtml(code) +
      '</span>' +
      '<span class="block-search-card__stone">' +
      escapeHtml(stone) +
      '</span>' +
      (sqm != null
        ? '<span class="block-search-card__sqm">' + escapeHtml(sqm) + ' m² available</span>'
        : '') +
      '</span>' +
      '</a>'
    );
  }

  function sectionMarkup(title, body, extraClass) {
    return (
      '<section class="block-search-section' +
      (extraClass ? ' ' + escapeHtml(extraClass) : '') +
      '">' +
      '<h3 class="block-search-heading">' +
      escapeHtml(title) +
      '</h3>' +
      body +
      '</section>'
    );
  }

  function renderPredictiveStoneResults(instance, query) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    var matches = filterStoneTypes(query);
    if (!matches.length) {
      panel.innerHTML = '';
      panel.hidden = true;
      hidePredictiveNativeResults(instance, false);
      return;
    }

    var limit = panel.__emperorShowAllStones ? matches.length : CONFIG.stoneResultLimit;
    var visibleMatches = matches.slice(0, limit);
    var rows = visibleMatches.map(stoneRowMarkup).join('');
    var moreButton =
      matches.length > visibleMatches.length
        ? '<button class="block-search-more" type="button" data-block-search-show-all-stones>Show all ' +
          escapeHtml(formatInventoryNumber(matches.length)) +
          ' stones</button>'
        : '';

    panel.innerHTML = sectionMarkup(
      'Stones',
      '<div class="block-search-stones" role="list">' + rows + '</div>' + moreButton,
      'block-search-section--stones'
    );
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(
      instance,
      matches.length === 1 ? '1 stone match found.' : matches.length + ' stone matches found.'
    );
  }

  function renderPopularStoneRecommendations(instance) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    var stones = popularStoneTypes();
    if (!stones.length) {
      panel.innerHTML = '';
      panel.hidden = true;
      hidePredictiveNativeResults(instance, false);
      return;
    }

    panel.__emperorShowAllStones = false;
    panel.innerHTML = sectionMarkup(
      'Popular stones',
      '<div class="block-search-stones" role="list">' + stones.map(stoneRowMarkup).join('') + '</div>',
      'block-search-section--stones block-search-section--popular'
    );
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(
      instance,
      stones.length === 1 ? '1 popular stone available.' : stones.length + ' popular stones available.'
    );
  }

  function renderPredictiveStoneLoading(instance) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    panel.innerHTML = '<p class="block-search-status">Loading stones…</p>';
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(instance, 'Loading stone inventory…');
  }

  function renderBlocksStatus(instance, message) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    panel.innerHTML =
      '<div class="block-search-blocks-view">' +
      '<button class="block-search-back" type="button" data-block-search-back>Back to results</button>' +
      '<p class="block-search-status">' +
      escapeHtml(message) +
      '</p>' +
      '</div>';
    panel.hidden = false;
    if (typeof instance.open === 'function') instance.open();
  }

  function renderInlineStoneBlocks(instance, stoneName, blocks, options) {
    options = options || {};
    var panel = getPredictivePanel(instance);
    if (!panel) return Promise.resolve(0);
    var safeBlocks = uniqueBlocks(blocks);
    var requestedLimit = options.visibleLimit || CONFIG.inlineBlockInitialLimit;
    var shown = Math.min(safeBlocks.length, requestedLimit);
    var visibleBlocks = safeBlocks.slice(0, shown);

    if (!safeBlocks.length) {
      renderBlocksStatus(instance, 'No blocks available for “' + stoneName + '”.');
      announcePredictive(instance, 'No blocks available for ' + stoneName + '.');
      return Promise.resolve(0);
    }

    return resolveProductUrl(stoneName).then(function (productUrl) {
      if (options.sequence !== predictiveState.sequence) return 0;
      var baseUrl = productUrl || fallbackStoneSearchUrl(stoneName);
      var cards = visibleBlocks
        .map(function (block) {
          return blockCardMarkup(block, productUrl ? appendHash(baseUrl, blockHash(block)) : baseUrl);
        })
        .join('');
      var moreButton =
        safeBlocks.length > visibleBlocks.length
          ? '<button class="block-search-more" type="button" data-block-search-show-all-blocks="' +
            escapeHtml(stoneName) +
            '" data-block-search-block-limit="' +
            escapeHtml(Math.min(safeBlocks.length, visibleBlocks.length + CONFIG.inlineBlockChunkSize)) +
            '">Show ' +
            escapeHtml(formatInventoryNumber(Math.min(CONFIG.inlineBlockChunkSize, safeBlocks.length - visibleBlocks.length))) +
            ' more (' +
            escapeHtml(formatInventoryNumber(safeBlocks.length)) +
            ' total)</button>'
          : '';

      panel.innerHTML =
        '<div class="block-search-blocks-view">' +
        '<div class="block-search-blocks-view__header">' +
        '<button class="block-search-back" type="button" data-block-search-back>Back to results</button>' +
        '<span class="block-search-blocks-view__title">' +
        escapeHtml(stoneName) +
        '</span>' +
        '</div>' +
        sectionMarkup(
          'Blocks',
          '<div class="block-search-panel block-search-panel--scroll" role="list">' + cards + '</div>' + moreButton,
          'block-search-section--blocks'
        ) +
        '</div>';
      panel.hidden = false;
      if (typeof instance.open === 'function') instance.open();
      announcePredictive(
        instance,
        safeBlocks.length === 1 ? '1 block found for ' + stoneName + '.' : safeBlocks.length + ' blocks found for ' + stoneName + '.'
      );
      return visibleBlocks.length;
    });
  }

  function renderBlockList(el, query, blocks, options) {
    options = options || {};
    var safeBlocks = uniqueBlocks(blocks);
    return Promise.all(
      safeBlocks.map(function (block) {
        return resolveBlockUrl(block, query, options).then(function (href) {
          return blockCardMarkup(block, href);
        });
      })
    ).then(function (cards) {
      if (options.signal && options.signal.aborted) return;
      var body =
        '<div class="block-search-panel" role="group" aria-label="Block code matches">' +
        cards.join('') +
        '</div>';
      el.innerHTML = options.heading ? sectionMarkup(options.heading, body, 'block-search-section--blocks') : body;
      el.hidden = false;
      return safeBlocks.length;
    });
  }

  function renderNoMatch(el, query) {
    el.innerHTML =
      '<p class="block-search-empty">No stone matches block code “' +
      escapeHtml(query) +
      '”.</p>';
    el.hidden = false;
  }

  function renderChecking(el) {
    el.innerHTML = '<p class="block-search-status">Checking block code…</p>';
    el.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Public entrypoint
  // ---------------------------------------------------------------------------

  /**
   * Intercept a search query. Returns a Promise<boolean>:
   *   true  -> we handled it (block-code path); native search should NOT run.
   *   false -> not a block code (or lookup failed); let native search proceed.
   *
   * Wire this BEFORE Shopify's native search executes (see README.md).
   */
  function interceptSearch(rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!isBlockCodeLike(query)) {
      return Promise.resolve(false); // free text -> native product search
    }
    return lookupBlockCode(query).then(function (data) {
      // Network failure -> fail open to native search rather than block the shopper.
      if (!data) return false;
      // The server re-applies the same classifier; honour its decision.
      if (data.route === 'product-search') return false;
      var blocks = uniqueBlocks(data.blocks || []);
      if (data.matched && blocks.length === 1) {
        handleMatch(query, blocks[0]);
      } else if (data.matched && blocks.length > 1) {
        renderBlockList(createResultsPageContainer(), query, blocks);
      } else {
        handleNoMatch(query);
      }
      return true; // handled — native search must not fuzzy-fall onto a product
    });
  }

  function getPredictivePanel(instance) {
    var root = instance && instance.predictiveSearchResults;
    if (!root) return null;
    // CRITICAL: the panel must live OUTSIDE `predictiveSearchResults`. The native
    // component renders results via `predictiveSearchResults.innerHTML = markup`,
    // which DETACHES any panel placed inside it — so an async (block-code) card
    // that resolves after a native re-render would write into an orphaned node and
    // never appear (observed live: typing a block code showed nothing while a
    // direct render worked). Anchor the panel as a sibling just BEFORE the native
    // results, inside their shared parent, so it survives every native re-render.
    var host = root.parentNode || root;
    var panel = host.querySelector('[data-block-search-predictive]');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute('data-block-search-predictive', '');
      host.insertBefore(panel, root);
    }
    if (!panel.__emperorBlockSearchEvents) {
      panel.__emperorBlockSearchEvents = true;
      panel.addEventListener('click', function (event) {
        handlePredictivePanelClick(instance, event);
      });
    }
    return panel;
  }

  function hidePredictiveNativeResults(instance, hide) {
    if (!instance || !instance.predictiveSearchResults) return;
    var selectors = [
      '#predictive-search-results-groups-wrapper',
      '.predictive-search__text',
      '[data-predictive-search-live-region-count-value]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = instance.predictiveSearchResults.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) nodes[j].hidden = !!hide;
    }
  }

  function announcePredictive(instance, text) {
    if (instance && typeof instance.setLiveRegionText === 'function') {
      instance.setLiveRegionText(text);
    }
  }

  function nextPredictiveSequence() {
    predictiveState.sequence += 1;
    return predictiveState.sequence;
  }

  function stopPredictiveBlockLookup() {
    if (predictiveState.timer) clearTimeout(predictiveState.timer);
    if (predictiveState.controller) predictiveState.controller.abort();
    predictiveState.timer = null;
    predictiveState.controller = null;
  }

  function clearPredictiveInventoryResults(instance) {
    nextPredictiveSequence();
    stopPredictiveBlockLookup();
    var panel = getPredictivePanel(instance);
    if (panel) {
      panel.innerHTML = '';
      panel.hidden = true;
      panel.__emperorShowAllStones = false;
    }
    hidePredictiveNativeResults(instance, false);
  }

  function runPredictivePopularStones(instance) {
    var panel = getPredictivePanel(instance);
    if (panel) panel.__emperorShowAllStones = false;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();

    if (stoneTypesCache) {
      renderPopularStoneRecommendations(instance);
      return;
    }

    renderPredictiveStoneLoading(instance);
    ensureStoneTypes().then(function () {
      if (sequence !== predictiveState.sequence) return;
      if (normalizeInventoryTerm(instance.getQuery ? instance.getQuery() : '')) return;
      renderPopularStoneRecommendations(instance);
    });
  }

  function runPredictiveStoneSearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!query || isBlockCodeLike(query)) return;

    var panel = getPredictivePanel(instance);
    if (panel) panel.__emperorShowAllStones = false;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();

    if (stoneTypesCache) {
      renderPredictiveStoneResults(instance, query);
      return;
    }

    renderPredictiveStoneLoading(instance);
    ensureStoneTypes().then(function () {
      if (sequence !== predictiveState.sequence) return;
      if ((instance.getQuery ? instance.getQuery() : query) !== query) return;
      renderPredictiveStoneResults(instance, query);
    });
  }

  function runPredictiveBlockSearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!isBlockCodeLike(query)) return;

    var sequence = nextPredictiveSequence();
    if (predictiveState.timer) clearTimeout(predictiveState.timer);
    if (predictiveState.controller) predictiveState.controller.abort();
    predictiveState.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    var panel = getPredictivePanel(instance);
    if (!panel) return;
    renderChecking(panel);
    hidePredictiveNativeResults(instance, true);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(instance, 'Checking block code…');

    predictiveState.timer = setTimeout(function () {
      var controller = predictiveState.controller;
      lookupBlockCode(query, { signal: controller ? controller.signal : undefined }).then(function (data) {
        if (controller && controller.signal.aborted) return;
        if (sequence !== predictiveState.sequence) return;
        if (!isBlockCodeLike(instance.getQuery ? instance.getQuery() : query)) return;
        if (!data) {
          panel.innerHTML = '';
          panel.hidden = true;
          hidePredictiveNativeResults(instance, false);
          return;
        }
        if (data.route === 'product-search') {
          clearPredictiveBlockResults(instance);
          return;
        }
        var blocks = uniqueBlocks(data.blocks || []);
        if (data.matched && blocks.length > 0) {
          hidePredictiveNativeResults(instance, true);
          renderBlockList(panel, query, blocks, {
            signal: controller ? controller.signal : undefined,
            heading: 'Blocks',
          }).then(function (count) {
            if (!count) return;
            announcePredictive(
              instance,
              count === 1 ? '1 block code match found.' : count + ' block code matches found.'
            );
          });
        } else {
          hidePredictiveNativeResults(instance, true);
          renderNoMatch(panel, query);
          announcePredictive(instance, 'No block code matches found.');
        }
        if (typeof instance.open === 'function') instance.open();
      });
    }, CONFIG.predictiveDebounceMs);
  }

  function runPredictiveInventorySearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!query) {
      runPredictivePopularStones(instance);
      return;
    }
    if (isBlockCodeLike(query)) {
      runPredictiveBlockSearch(instance, query);
    } else {
      runPredictiveStoneSearch(instance, query);
    }
  }

  function expandPredictiveStone(instance, stoneName, options) {
    options = options || {};
    var stone = String(stoneName || '').trim();
    if (!stone) return;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();
    renderBlocksStatus(instance, 'Loading blocks…');
    announcePredictive(instance, 'Loading blocks for ' + stone + '…');

    lookupBlocksForStone(stone).then(function (blocks) {
      if (sequence !== predictiveState.sequence) return;
      if (blocks === null) {
        renderBlocksStatus(instance, 'Unable to load blocks for “' + stone + '”.');
        announcePredictive(instance, 'Unable to load blocks for ' + stone + '.');
        return;
      }
      renderInlineStoneBlocks(instance, stone, blocks, {
        sequence: sequence,
        visibleLimit: options.visibleLimit || CONFIG.inlineBlockInitialLimit,
      });
    });
  }

  function handlePredictivePanelClick(instance, event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var stoneLink = target.closest('[data-block-search-stone]');
    if (stoneLink) {
      event.preventDefault();
      expandPredictiveStone(instance, stoneLink.getAttribute('data-block-search-stone'));
      return;
    }

    var backButton = target.closest('[data-block-search-back]');
    if (backButton) {
      event.preventDefault();
      runPredictiveInventorySearch(instance, instance.getQuery ? instance.getQuery() : '');
      return;
    }

    var showAllStones = target.closest('[data-block-search-show-all-stones]');
    if (showAllStones) {
      event.preventDefault();
      var panel = getPredictivePanel(instance);
      if (panel) panel.__emperorShowAllStones = true;
      renderPredictiveStoneResults(instance, instance.getQuery ? instance.getQuery() : '');
      return;
    }

    var showAllBlocks = target.closest('[data-block-search-show-all-blocks]');
    if (showAllBlocks) {
      event.preventDefault();
      expandPredictiveStone(instance, showAllBlocks.getAttribute('data-block-search-show-all-blocks'), {
        visibleLimit: Number(showAllBlocks.getAttribute('data-block-search-block-limit')) || CONFIG.inlineBlockInitialLimit,
      });
    }
  }

  // Expose for the theme to call, and for testing.
  window.EmperorBlockSearch = {
    isBlockCodeLike: isBlockCodeLike,
    interceptSearch: interceptSearch,
    lookupBlockCode: lookupBlockCode,
    ensureStoneTypes: ensureStoneTypes,
    filterStoneTypes: filterStoneTypes,
    lookupBlocksForStone: lookupBlocksForStone,
    resolveProductUrl: resolveProductUrl,
    resolveBlockUrl: resolveBlockUrl,
    renderPredictiveBlockSearch: runPredictiveInventorySearch,
    renderPredictiveInventorySearch: runPredictiveInventorySearch,
    config: CONFIG,
  };

  // ---------------------------------------------------------------------------
  // Auto-wiring (search RESULTS page)
  // ---------------------------------------------------------------------------
  //
  // On the search results page (/search?q=...), if the query is a block code
  // we take over before the shopper reads the (wrong) fuzzy results. This runs
  // as soon as the script loads. For the search-AS-YOU-TYPE box, wire
  // interceptSearch() on submit instead — see README.md.

  function autoWireResultsPage() {
    if (!/\/search\b/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    if (!isBlockCodeLike(q)) return; // native search already correct for text
    // Optionally hide the wrong native results immediately while we resolve.
    suppressNativeResults();
    interceptSearch(q).then(function (handled) {
      // If not handled (failed open), native results remain — unhide if hidden.
      if (!handled && CONFIG.nativeResultsSelector) {
        var nodes = document.querySelectorAll(CONFIG.nativeResultsSelector);
        for (var i = 0; i < nodes.length; i++) nodes[i].hidden = false;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Auto-wiring (search FORM submit)
  // ---------------------------------------------------------------------------
  //
  // Intercept on submit so a block-code query is resolved BEFORE the browser
  // navigates to the (wrong) native /search results. The results-page form
  // carries `data-block-search`; predictive header/template forms are covered
  // by the custom element selector. We FAIL OPEN: free text submits natively,
  // and any lookup error lets the original submit proceed.

  function wireForm(form) {
    if (!form || form.__emperorBlockSearchWired) return;
    form.__emperorBlockSearchWired = true;
    form.addEventListener('submit', function (e) {
      var input = form.querySelector('input[name="q"]');
      var q = input ? String(input.value || '').trim() : '';
      if (!isBlockCodeLike(q)) return; // free text -> let native search submit
      e.preventDefault();
      interceptSearch(q).then(function (handled) {
        // Failed open (network error / not a block code) -> native search.
        if (!handled) form.submit();
      });
    });
  }

  function autoWireForms() {
    var forms = document.querySelectorAll('form[data-block-search], predictive-search form[action*="/search"]');
    for (var i = 0; i < forms.length; i++) wireForm(forms[i]);
  }

  function patchPredictiveSearch() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('predictive-search').then(function () {
      var Ctor = customElements.get('predictive-search');
      if (!Ctor || !Ctor.prototype || Ctor.prototype.__emperorBlockSearchPatched) return;
      Ctor.prototype.__emperorBlockSearchPatched = true;

      var originalOnChange = Ctor.prototype.onChange;
      Ctor.prototype.onChange = function () {
        originalOnChange.apply(this, arguments);
        runPredictiveInventorySearch(this, this.getQuery());
      };

      var originalOnFocus = Ctor.prototype.onFocus;
      Ctor.prototype.onFocus = function () {
        originalOnFocus.apply(this, arguments);
        ensureStoneTypes();
        runPredictiveInventorySearch(this, this.getQuery ? this.getQuery() : '');
      };

      var originalRenderSearchResults = Ctor.prototype.renderSearchResults;
      Ctor.prototype.renderSearchResults = function () {
        originalRenderSearchResults.apply(this, arguments);
        runPredictiveInventorySearch(this, this.getQuery());
      };

      var originalCloseResults = Ctor.prototype.closeResults;
      Ctor.prototype.closeResults = function () {
        clearPredictiveInventoryResults(this);
        originalCloseResults.apply(this, arguments);
      };
    });
  }

  function init() {
    autoWireResultsPage();
    autoWireForms();
    patchPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
